// File: src/pages/StudentEvaluation/StudentAccountDetails.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { tokens } from "../../theme";
import useStudentEvaluationService from "../../api/services/StudentEvaluationService";
import useStudentService from "../../api/services/studentService";
import useTermService from "../../api/services/termService";
import Header from "../../components/Header";
import StudentEvaluationDialog from "./StudentEvaluationDialog";
import EditStudentEvaluation from "./EditStudentEvaluation";
import DeleteDialog from "./DeleteDialog";

const StudentTermEvaluationTable = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { studentId } = useParams();

  const { getEvaluationsByStudent } = useStudentEvaluationService();
  const { loadStudentDetails } = useStudentService();
  const { fetchAllActiveTerms, terms } = useTermService();

  const [evaluations, setEvaluations] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);

  // Fetch student info
  const loadStudent = async () => {
    try {
      const data = await loadStudentDetails(studentId);
      setSelectedStudent(data);
    } catch (err) {
      toast.error("Failed to fetch student information");
    }
  };

  // Fetch student evaluations
  const loadEvaluations = async () => {
    try {
      setLoading(true);
      const data = await getEvaluationsByStudent(studentId);
      console.log("evaluation data  are " , data);
      setEvaluations(data || []);
    } catch (error) {
      toast.error("Failed to fetch student evaluations");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) {
      loadStudent();
      loadEvaluations();
      fetchAllActiveTerms();
    }
  }, [studentId]);

  // Handle edit
  const handleEditRow = (rowData) => {
    setEditingRow(rowData);
    setEditDialogOpen(true);
  };

  // Handle delete
  const handleDeleteRow = (rowData) => {
    setDeletingRow(rowData);
    setDeleteDialogOpen(true);
  };

  // Define DataGrid columns
  const columns = useMemo(
    () => [
      { field: "termName", headerName: "Term", flex: 1, minWidth: 150 },
      { field: "spiritualLife", headerName: "Spiritual Life", flex: 1, minWidth: 130 },
      { field: "academicLife", headerName: "Academic Life", flex: 1, minWidth: 130 },
      { field: "manualWork", headerName: "Manual Work", flex: 1, minWidth: 130 },
      { field: "behaviour", headerName: "behaviour", flex: 1, minWidth: 130 },
      { field: "health", headerName: "Health", flex: 1, minWidth: 130 },
      { field: "leadershipSkills", headerName: "Leadership Skills", flex: 1, minWidth: 160 },
      { field: "sports", headerName: "Sports", flex: 1, minWidth: 120 },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Edit Evaluation">
              <IconButton
                size="small"
                onClick={() => handleEditRow(params.row)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Evaluation">
              <IconButton
                size="small"
                onClick={() => handleDeleteRow(params.row)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  // Prepare DataGrid rows
  const rows = evaluations.map((item, index) => {
    const termName = item.term?.termName || item.termName || "N/A";
    return {
      id: index + 1,
      termName: termName,
      spiritualLife: item.spiritualLife || "",
      academicLife: item.academicLife || "",
      manualWork: item.manualWork || "",
      health: item.health || "",
      behaviour: item.behaviour || "",
      leadershipSkills: item.leadershipSkills || "",
      sports: item.sports || "",
      originalData: item,
    };
  });

  return (
    <Box m="20px">
      <Header
        title="STUDENT EVALUATION REPORT"
        subtitle="View and manage student evaluations per term"
      />

      {/* Header Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" color={colors.grey[100]}>
          Student: {selectedStudent?.studentName || "Loading..."}
        </Typography>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => setDialogOpen(true)}
          disabled={!selectedStudent || !terms || terms.length === 0}
        >
          Add / Update Evaluation
        </Button>
      </Box>

      {/* Data Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <Box
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? colors.blueAccent[100]
                  : colors.blueAccent[700],
              color: theme.palette.mode === "light" ? "#000" : "#fff",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
          />
        </Box>
      )}

      {/* Add Evaluation Dialog */}
      {dialogOpen && selectedStudent && (
        <StudentEvaluationDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingRow(null);
          }}
          onSuccess={() => {
            setDialogOpen(false);
            setEditingRow(null);
            setTimeout(() => {
              loadEvaluations();
            }, 500);
          }}
          students={[selectedStudent]}
          terms={terms || []}
          selectedStudent={selectedStudent}
        />
      )}

      {/* Edit Evaluation Dialog */}
      {editDialogOpen && editingRow && (
        <EditStudentEvaluation
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingRow(null);
          }}
          onSuccess={() => {
            setEditDialogOpen(false);
            setEditingRow(null);
            setTimeout(() => {
              loadEvaluations();
            }, 500);
          }}
          editingData={editingRow}
        />
      )}

      {/* Delete Evaluation Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingRow(null);
        }}
        deletingData={deletingRow}
        onSuccess={() => {
          setDeleteDialogOpen(false);
          setDeletingRow(null);
          setTimeout(() => {
            loadEvaluations();
          }, 500);
        }}
      />
    </Box>
  );
};

export default StudentTermEvaluationTable;
